import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import shiftApi from '../services/shiftApi';

interface ShiftLockoutWarningProps {
  shiftId: number;
  lockoutUntil?: string;
  isAdmin?: boolean;
  onAdminOverride?: () => void;
}

const ShiftLockoutWarning: React.FC<ShiftLockoutWarningProps> = ({
  shiftId,
  lockoutUntil,
  isAdmin = false,
  onAdminOverride
}) => {
  const [remainingTime, setRemainingTime] = useState<string>('');
  const [canLogout, setCanLogout] = useState(false);

  useEffect(() => {
    const updateRemainingTime = () => {
      if (!lockoutUntil) {
        setCanLogout(true);
        setRemainingTime('');
        return;
      }

      const now = new Date().getTime();
      const lockout = new Date(lockoutUntil).getTime();
      const diff = lockout - now;

      if (diff <= 0) {
        setCanLogout(true);
        setRemainingTime('');
      } else {
        setCanLogout(false);
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setRemainingTime(`${hours}h ${minutes}m`);
      }
    };

    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [lockoutUntil]);

  if (canLogout || isAdmin) {
    return null;
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
      <div className="flex items-start">
        <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-medium text-yellow-800 mb-1">
            Logout Locked
          </h4>
          <p className="text-sm text-yellow-700">
            You cannot logout for the next <span className="font-medium">{remainingTime}</span>.
            This is to ensure accurate time tracking for your shift.
          </p>
          {isAdmin && onAdminOverride && (
            <button
              onClick={onAdminOverride}
              className="mt-2 text-sm text-yellow-800 underline hover:text-yellow-900"
            >
              Admin Override
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShiftLockoutWarning;

